import type { IconProps } from '@carboncopyco/icons';
import { CloseOutlineIcon } from '@carboncopyco/icons';
import classNames from 'classnames';
import React, { isValidElement } from 'react';
import type { BgColors, BorderColors, Colors } from '../../constants';
import { TextCaption } from '../Typography';

export interface MessageBoxProps {
  handleClose?: ((event: React.MouseEvent) => void) | undefined;
  close?: boolean;
  danger?: boolean;
  warn?: boolean;
  info?: boolean;
  success?: boolean;
  label?: string | React.ReactNode;
  leftIcon?: React.ReactElement<IconProps>;
  fullHeight?: boolean;
  roundedCorners?: boolean;
  border?: boolean;
  borderColor?: BorderColors;
}

const bgColor: Record<string, BgColors> = {
  normal: 'bg-ds-natural-50',
  danger: 'bg-ds-danger-50',
  info: 'bg-ds-primary-50',
  success: 'bg-ds-accent2-50',
  warn: 'bg-ds-secondary-50',
};

const textColor: Record<string, Colors> = {
  normal: 'text-ds-natural-600',
  danger: 'text-ds-danger-500',
  info: 'text-ds-primary-500',
  success: 'text-ds-accent2-500',
  warn: 'text-ds-secondary-500',
};

const outlineColor = {
  normal: 'outline-ds-natural-200',
  danger: 'outline-ds-danger-200',
  info: 'outline-ds-primary-200',
  success: 'outline-ds-accent2-200',
  warn: 'outline-ds-secondary-200',
};

const getTheme = ({
  danger,
  warn,
  success,
  info,
}: Partial<MessageBoxProps>) => {
  if (danger) {
    return 'danger';
  }
  if (warn) {
    return 'warn';
  }
  if (success) {
    return 'success';
  }
  if (info) {
    return 'info';
  }
  return 'normal';
};

export const MessageBox = ({
  handleClose,
  close,
  label,
  leftIcon,
  fullHeight,
  roundedCorners = true,
  border,
  ...rest
}: MessageBoxProps) => {
  const theme = getTheme(rest);
  return (
    <div
      className={classNames(
        'flex items-center py-1.5 px-2.5',
        bgColor[theme],
        fullHeight && 'h-full',
        roundedCorners && 'rounded-md',
        border &&
          `outline outline-offset-[-1px] outline-1 ${outlineColor[theme]}`
      )}
    >
      {leftIcon && (
        <div className={classNames('mr-2', textColor[theme])}>
          {React.cloneElement(leftIcon, {
            size: 16,
          })}
        </div>
      )}
      {isValidElement(label) ? (
        label
      ) : (
        <TextCaption
          label={label}
          color={textColor[theme]}
        />
      )}
      {close && (
        <div
          className="pl-3 pr-0 ml-auto cursor-pointer text-ds-natural-600"
          onClick={handleClose}
        >
          <CloseOutlineIcon size={16} />
        </div>
      )}
    </div>
  );
};
